import {useCallback, useEffect, useRef, useState} from "react";
import Api from "../../../api";
import { JobProps } from "utils/types";
import { shouldShowJobInRateFilter } from "../../../utils/rateCalculations";
import { isPhoneNotVerifiedError, onPhoneVerified } from "../../../utils/phoneVerificationGate";

export interface FiltersProps {
    selectedSort: number,
    favourite: boolean,
    distanceIdx: number,
    rateIdx: number,
    date?: string,
    withDate?: boolean,
    shiftTypeIdx?: number,
}

export const sortOptions = [
    'Newest',
    'Soonest',
    'Closest',
];

export const distanceOptionsUS = [
    'Any Distance',
    '10 miles',
    '25 miles',
    '50 miles',
    '60+ miles',
];

export const distanceOptionsCA = [
    'Any Distance',
    'Within 16 km',
    'Within 40 km',
    'Within 80 km',
    'Within 95 km',
];

export const rateOptsHyg = [
    'All Hourly Rates',
    '$35/hr +',
    '$40/hr +',
    '$45/hr +',
    '$50/hr +',
    '$55/hr +',
];

export const rateOptsAst = [
    'All Hourly Rates',
    '$20/hr +',
    '$24/hr +',
    '$28/hr +',
    '$32/hr +',
    '$35/hr +',
];

const sortByDistance = (jobA: JobProps, jobB: JobProps) => {
    const disA = parseFloat(jobA.distance);
    const disB = parseFloat(jobB.distance);
    return disA > disB
        ? 1
        : disB > disA
            ? -1 : 0;
}

const sortByNewest = (jobA: JobProps, jobB: JobProps) => {
    // postedOn
    const disA = jobA.postedOn;
    const disB = jobB.postedOn;
    return disA < disB
        ? 1
        : disB < disA
            ? -1 : 0;
}

const sortBySoonest = (jobA: JobProps, jobB: JobProps) => {
    // startDate
    const dateA = new Date(jobA.startDate.replace(/-/g, "/") + ' ' + jobA.shifts[0].postedStart);
    const dateB = new Date(jobB.startDate.replace(/-/g, "/") + ' ' + jobB.shifts[0].postedStart);
    return dateA > dateB
        ? 1
        : dateB > dateA
            ? -1 : 0;
}

export const sorting = (selectedSort: number, availableJobs: JobProps[]): JobProps[] => {
    if (selectedSort === 0 && availableJobs.length) {
        return [...availableJobs?.sort(sortByNewest)]
    }
    if (selectedSort === 1 && availableJobs.length) {
        return [...availableJobs?.sort(sortBySoonest)]
    }
    if (selectedSort === 2 && availableJobs.length) {
        return [...availableJobs?.sort(sortByDistance)]
    }
    return [...availableJobs];
}

export const filterByDistance = (distanceIdx: number, jobs: JobProps[]): JobProps[] => {
    const distances = [
        '',
        '16',
        '40',
        '80',
        '95',
    ];

    const parsedDistance = parseFloat(distances[distanceIdx]) || 110;
    return jobs.filter(job => parseFloat(job.distance) <= parsedDistance);
}

/**
 * Filters jobs by rate based on the talent's personalized rate range.
 * 
 * Logic: Show the shift if its yellow range (or below) meets the filter minimum.
 * If ONLY red values would fall in the filter range, do NOT show the shift.
 * 
 * @param status - Talent's status (0=Probation, 1=Pro, 2=Elite)
 * @param rateIdx - Selected filter index
 * @param jobs - Array of jobs to filter
 * @param rateOpts - Rate options array (e.g., ['All Hourly Rates', '$35/hr +', ...])
 * @returns Filtered array of jobs
 */
export const filterByRate = (status: number, rateIdx: number, jobs: JobProps[], rateOpts: string[]): JobProps[] => {
    // If "All Hourly Rates" is selected (index 0), return all jobs
    if (rateIdx === 0) {
        return jobs;
    }
    
    const parsedRate = parseInt(rateOpts[rateIdx].slice(1)) || 0;
    
    return jobs.filter(job => 
        shouldShowJobInRateFilter(job.proposedRate, status, parsedRate)
    );
}

export function useAvailableJobs(type: number, date?: string, locationId?: number, excludeJobId?: number, includeAdmin?: boolean, enabled: boolean = true): [JobProps[], JobProps[], string | null, boolean, (includeAdminOverride?: boolean, background?: boolean) => Promise<void>, boolean] {
    const [availableJobs, setAvailableJobs] = useState<JobProps[]>([])
    // Stored as text: the list renders it, and an error object there crashed the page.
    const [errorGetJos, setErrorGetJobs] = useState<string | null>(null);
    // T2-7517: the server refused shift data until the talent verifies their phone.
    const [phoneVerificationRequired, setPhoneVerificationRequired] = useState(false);
    const [loadingJobs, setLoadingJobs] = useState(true);
    const [hidedJobs, setHidedJobs] = useState<JobProps[]>([]);
    const latestRequestId = useRef(0);
    
    const getAvailableJobs = useCallback(async (includeAdminOverride?: boolean, background?: boolean) => {
        const requestId = ++latestRequestId.current;
        if (!enabled) {
            setAvailableJobs([]);
            setHidedJobs([]);
            setErrorGetJobs(null);
            setLoadingJobs(false);
            return;
        }
        try {
            setLoadingJobs(true);
            setErrorGetJobs(null);
            const shifts = await Api.getAvailableJobs(
                type,
                !!date ? date : undefined,
                locationId,
                includeAdminOverride ?? includeAdmin,
                { background: !!background },
            );
            if (requestId !== latestRequestId.current) return;
            setPhoneVerificationRequired(false);
            // Filter out expired/removed offers
            let availableShifts = shifts.filter((shift: JobProps) => shift.offer_status !== 3)
            if (excludeJobId) {
                availableShifts = availableShifts.filter((shift: JobProps) => shift.id !== excludeJobId);
            }
            setHidedJobs(availableShifts.filter((shift: JobProps) => shift.isHided));
            setAvailableJobs(availableShifts.filter((shift: JobProps) => !shift.isHided));
            // NOTE: "view shifts" tracking has been moved to AvailabeJobsPage.tsx
            // for richer context (filter state, hygienist data, aggregated metrics).
            // See the useEffect with hasTrackedViewShiftsRef in AvailabeJobsPage.tsx
        } catch (e: any) {
            if (requestId === latestRequestId.current) {
                if (isPhoneNotVerifiedError(e)) {
                    setPhoneVerificationRequired(true);
                    setAvailableJobs([]);
                    setHidedJobs([]);
                } else {
                    setErrorGetJobs(String(e?.userMessage || e?.message || 'Unable to load shifts. Please try again.'));
                }
            }
        } finally {
            if (requestId === latestRequestId.current) {
                setLoadingJobs(false);
            }
        }
    }, [date, type, locationId, excludeJobId, includeAdmin, enabled]);

    useEffect(() => () => {
        latestRequestId.current += 1;
    }, []);
    
    useEffect(() => {
        getAvailableJobs();
    }, [getAvailableJobs]);

    // Refetch when returning to the page (focus, visibilitychange, pageshow)
    useEffect(() => {
        if (!enabled) return;
        // Refreshes the member did not ask for are background loads (T2-7517).
        const handleFocus = () => {
            getAvailableJobs(undefined, true);
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                getAvailableJobs(undefined, true);
            }
        };
        const handlePageShow = (_e: any) => {
            getAvailableJobs(undefined, true);
        };
        const handlePopState = (_e: any) => {
            getAvailableJobs(undefined, true);
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [enabled, getAvailableJobs]);

    useEffect(() => {
        if (!enabled) return undefined;
        return onPhoneVerified(() => { getAvailableJobs(undefined, true); });
    }, [enabled, getAvailableJobs]);
    
    return [availableJobs, hidedJobs, errorGetJos, loadingJobs, getAvailableJobs, phoneVerificationRequired];
}
